import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const projects = [
  {
    title: 'Enterprise Resource Planning',
    category: 'Enterprise Software',
    description: 'A comprehensive ERP solution that streamlines operations and enhances productivity for large organizations.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  },
  {
    title: 'Cloud Migration Platform',
    category: 'Cloud Solutions',
    description: 'Enterprise-grade platform for seamless cloud migration and infrastructure management.',
    image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  },
  {
    title: 'Data Analytics Suite',
    category: 'Data Analytics',
    description: 'Advanced analytics platform providing real-time insights and predictive capabilities.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  },
  {
    title: 'API Management System',
    category: 'API Development',
    description: 'Comprehensive API management solution for enterprise-scale integration and security.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  },
  {
    title: 'Security Platform',
    category: 'Security Solutions',
    description: 'Enterprise-grade security platform with advanced threat detection and prevention.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  },
  {
    title: 'SaaS Platform',
    category: 'SaaS Solutions',
    description: 'Scalable SaaS platform delivering enterprise-grade software as a service.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
  },
];

const categories = ['All', ...new Set(projects.map(project => project.category))];

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const filteredProjects = selectedCategory === 'All'
    ? projects.slice(1)
    : projects.slice(1).filter(project => project.category === selectedCategory);
  const featuredProject = projects[0];
  return (
    <>
      <Helmet>
        <title>Portfolio | Nayrix Projects & Case Studies</title>
        <meta name="description" content="See Nayrix's portfolio of enterprise software, SaaS, and cloud projects for global clients." />
        <meta property="og:title" content="Portfolio | Nayrix Projects & Case Studies" />
        <meta property="og:description" content="See Nayrix's portfolio of enterprise software, SaaS, and cloud projects for global clients." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nayrix.com/portfolio" />
        <link rel="canonical" href="https://nayrix.com/portfolio" />
      </Helmet>
      <main className="relative">
        {/* Hero/Intro Section */}
        <section className="relative py-24 bg-gradient-to-br from-primary-50 via-light-bg to-primary-100 dark:from-dark-bg dark:via-dark-bg/80 dark:to-dark-bg/60 animate-gradient-move">
          <div className="container px-4 mx-auto text-center max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold mb-6 dark:text-dark-text text-light-text"
            >
              Our Portfolio
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl dark:text-dark-text-secondary text-light-text-secondary mb-8"
            >
              Explore our portfolio of enterprise-grade software solutions, crafted for innovation and impact.
            </motion.p>
          </div>
        </section>
        {/* Featured Project */}
        <section className="py-12 bg-light-bg dark:bg-dark-bg/50">
          <div className="container px-4 mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 dark:text-dark-text text-light-text text-center">Featured Project</h2>
              <div className="flex flex-col md:flex-row items-center gap-8 bg-white/60 dark:bg-dark-bg/60 rounded-2xl shadow-xl border dark:border-dark-border border-light-border backdrop-blur-md p-8">
                <div className="flex-1">
                  <img src={featuredProject.image} alt={featuredProject.title} className="rounded-xl w-full object-cover aspect-video shadow-lg" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <span className="text-sm dark:text-dark-accent text-light-accent mb-2 block">{featuredProject.category}</span>
                  <h3 className="text-2xl font-semibold mb-2 dark:text-dark-text text-light-text">{featuredProject.title}</h3>
                  <p className="dark:text-dark-text-secondary text-light-text-secondary mb-4">{featuredProject.description}</p>
                  <button
                    onClick={() => setSelectedProject(featuredProject)}
                    className="inline-block px-8 py-3 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors duration-300 mt-2 font-medium shadow-lg"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </motion.div>
            {/* Category Filter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-wrap justify-center gap-4 mb-12"
            >
              {categories.map((category) => (
                <motion.button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'dark:bg-dark-accent bg-light-accent text-white'
                      : 'dark:bg-dark-bg bg-light-bg dark:text-dark-text-secondary text-light-text-secondary dark:border-dark-border border-light-border border'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {category}
                </motion.button>
              ))}
            </motion.div>
            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              <AnimatePresence mode="wait">
                {filteredProjects.map((project, index) => (
                  <motion.div
                    key={project.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white/60 dark:bg-dark-bg/60 rounded-2xl shadow-xl border dark:border-dark-border border-light-border backdrop-blur-md overflow-hidden group cursor-pointer hover:scale-[1.025] hover:shadow-2xl transition-all duration-300"
                    onClick={() => setSelectedProject(project)}
                    tabIndex={0}
                    role="button"
                    aria-label={`View details for ${project.title}`}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedProject(project); }}
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-70 group-hover:opacity-80 transition-opacity duration-300" />
                    </div>
                    <div className="p-6">
                      <span className="text-sm dark:text-dark-accent text-light-accent mb-2 block">
                        {project.category}
                      </span>
                      <h3 className="text-xl font-semibold mb-2 dark:text-dark-text text-light-text">
                        {project.title}
                      </h3>
                      <p className="dark:text-dark-text-secondary text-light-text-secondary">
                        {project.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            {/* Modal for Project Quick-View */}
            <AnimatePresence>
              {selectedProject && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
                  onClick={() => setSelectedProject(null)}
                  aria-modal="true"
                  role="dialog"
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-light-bg dark:bg-dark-bg rounded-2xl max-w-2xl w-full shadow-2xl border dark:border-dark-border border-light-border"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="relative aspect-video overflow-hidden rounded-t-2xl">
                      <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-8">
                      <span className="text-sm dark:text-dark-accent text-light-accent mb-2 block">{selectedProject.category}</span>
                      <h3 className="text-2xl font-semibold mb-4 dark:text-dark-text text-light-text">{selectedProject.title}</h3>
                      <p className="dark:text-dark-text-secondary text-light-text-secondary mb-6">{selectedProject.description}</p>
                      <button
                        onClick={() => setSelectedProject(null)}
                        className="px-6 py-2 rounded-full border dark:border-dark-border dark:text-dark-text border-light-border text-light-text border-2 transition-colors duration-300"
                      >
                        Close
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </main>
    </>
  );
};

export default Portfolio;

export { projects }; 